import React, { useState, useEffect } from 'react';
import { Globe, Search, ShieldCheck, RefreshCw, Trash2, Plus, ExternalLink, Lock, AlertCircle, X } from 'lucide-react';
import { moduleService } from '../../../services/modules';
import { MiniAppShell } from './MiniAppShell';
import { DomainAsset, DomainSearchResult } from '../../../modules/domain-manager/api';
import { PortfolioView } from './PortfolioView';

interface DomainsAppProps {
    onExecutionStarted: (executionId: string) => void;
    onBack: () => void;
}

export const DomainsApp = ({ onExecutionStarted, onBack }: DomainsAppProps) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
    const navItems = [
        { id: 'dashboard', label: 'Portfolio', icon: Globe },
        { id: 'search', label: 'Search & Buy', icon: Search },
        { id: 'dns', label: 'DNS Manager', icon: ShieldCheck },
        { id: 'lifecycle', label: 'Lifecycle Hub', icon: RefreshCw },
    ];

    return (
        <MiniAppShell
            title="Secure Domains"
            icon={Globe}
            navItems={navItems}
            onBack={onBack}
        >
            {(activeTab, setActiveTab) => {
                switch (activeTab) {
                    case 'dashboard':
                        return <PortfolioView
                            onExecutionStarted={onExecutionStarted}
                            setActiveTab={setActiveTab}
                            onQuickSearch={(q) => {
                                setSearchQuery(q);
                                setActiveTab('search');
                            }}
                        />;
                    case 'search':
                        return <SearchView
                            onExecutionStarted={onExecutionStarted}
                            initialQuery={searchQuery}
                            setInitialQuery={setSearchQuery}
                            onBuy={(domain) => {
                                setSelectedDomain(domain);
                                setActiveTab('checkout');
                            }}
                        />;
                    case 'checkout':
                        return <CheckoutView
                            domain={selectedDomain!}
                            onExecutionStarted={onExecutionStarted}
                            onBack={() => setActiveTab('search')}
                        />;
                    case 'dns':
                        return <DNSView onExecutionStarted={onExecutionStarted} />;
                    case 'lifecycle':
                        return <LifecycleView onExecutionStarted={onExecutionStarted} />;
                    default:
                        return <PortfolioView
                            onExecutionStarted={onExecutionStarted}
                            setActiveTab={setActiveTab}
                            onQuickSearch={(q) => {
                                setSearchQuery(q);
                                setActiveTab('search');
                            }}
                        />;
                }
            }}
        </MiniAppShell>
    );
};

export const SearchView = ({ onExecutionStarted, initialQuery, setInitialQuery, onBuy }: { onExecutionStarted: (id: string) => void, initialQuery: string, setInitialQuery: (q: string) => void, onBuy: (domain: string) => void }) => {
    const [query, setQuery] = useState(initialQuery);
    const [results, setResults] = useState<DomainSearchResult[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setQuery(initialQuery);
    }, [initialQuery]);

    const tldSuggestions = ['.com', '.ai', '.io', '.co.za', '.net', '.org'];

    const handleSearch = async (searchTerm?: string) => {
        const term = searchTerm || query;
        if (!term) {return;}
        setLoading(true);
        try {
            const { result } = await moduleService.executeModule('domain_manager', {
                action: 'searchDomains',
                keywords: [term],
            });
            setResults(result);
        } catch (err: any) {
            alert(`Search failed: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleTldClick = (tld: string) => {
        const baseName = query.includes('.') ? query.split('.')[0] : query;
        const fullQuery = `${baseName}${tld}`;
        setQuery(fullQuery);
        setInitialQuery(fullQuery);
        handleSearch(fullQuery);
    };

    return (
        <div className="space-y-8">
            <div className="max-w-2xl mx-auto text-center space-y-4">
                <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Find Your Perfect Domain</h2>
                <p className="text-sm text-slate-400 font-medium">Instant availability check across all major TLDs</p>
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                        <input
                            value={query}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setInitialQuery(e.target.value);
                            }}
                            placeholder="Enter your dream domain..."
                            className="w-full pl-12 pr-4 py-4 rounded-2xl border border-slate-200 text-sm font-medium focus:ring-2 ring-blue-500 outline-none transition-all bg-white"
                        />
                    </div>
                    <button
                        onClick={() => handleSearch()}
                        disabled={loading}
                        className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                        {loading ? <RefreshCw size={18} className="animate-spin" /> : 'Search'}
                    </button>
                </div>

                {query && (
                    <div className="flex flex-wrap justify-center gap-2 pt-2">
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest self-center mr-1">Popular:</span>
                        {tldSuggestions.map((tld) => (
                            <button
                                key={tld}
                                onClick={() => handleTldClick(tld)}
                                className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-600 hover:border-blue-400 hover:text-blue-600 hover:shadow-sm transition-all"
                            >
                                {tld}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="max-w-2xl mx-auto space-y-3">
                {results.map((res, i) => (
                    <div key={i} className="p-4 bg-white border border-slate-100 rounded-2xl flex items-center justify-between hover:border-blue-500 transition-all shadow-sm">
                        <div className="flex items-center gap-3">
                            <Globe size={18} className="text-slate-300" />
                            <span className="text-sm font-bold text-slate-700">{res.domain}</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className={`text-xs font-bold ${res.available ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {res.available ? `${res.price} ${res.currency}` : 'Unavailable'}
                            </span>
                            {res.available && (
                                <button
                                    onClick={() => onBuy(res.domain)}
                                    className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all"
                                >
                                    Buy Now
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DNSView = ({ onExecutionStarted }: { onExecutionStarted: (id: string) => void }) => {
    const [records, setRecords] = useState([
        { type: 'A', name: '@', value: '192.168.1.1', ttl: 3600 },
        { type: 'AAAA', name: '@', value: '2001:0db8:85a3:0000:0000:8a2e:0370:7334', ttl: 3600 },
        { type: 'MX', name: '@', value: 'mail.cloudflare.com', ttl: 3600 },
        { type: 'TXT', name: '@', value: 'v=spf1 include:_spf.google.com ~all', ttl: 3600 },
        { type: 'CNAME', name: 'www', value: 'ghs.googlehosted.com', ttl: 3600 },
    ]);
    const [isAdding, setIsAdding] = useState(false);
    const [editingRecord, setEditingRecord] = useState<any | null>(null);
    const [form, setForm] = useState({ type: 'A', name: '@', value: '', ttl: 3600 });

    const validateRecord = (type: string, value: string) => {
        if (type === 'A') {return /^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$/.test(value);}
        if (type === 'AAAA') {return /^(?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}$/i.test(value);}
        return value.length > 0;
    };

    const handleSave = async () => {
        if (!validateRecord(form.type, form.value)) {
            alert(`Invalid value for ${form.type} record`);
            return;
        }
        try {
            const result = await moduleService.executeModule('domain_manager', {
                action: 'updateDNS',
                projectId: 'current_project',
                records: [form],
            });
            onExecutionStarted(result.executionId);
            if (editingRecord) {
                setRecords(records.map(r => r === editingRecord ? form : r));
            } else {
                setRecords([...records, form]);
            }
            setIsAdding(false);
            setEditingRecord(null);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const startEdit = (record: any) => {
        setEditingRecord(record);
        setForm({ ...record });
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">DNS Command Center</h2>
                    <p className="text-xs text-slate-400 font-medium">Advanced record management for absolute control</p>
                </div>
                <button
                    onClick={() => { setForm({ type: 'A', name: '@', value: '', ttl: 3600 }); setIsAdding(true); }}
                    className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-slate-800 transition-all"
                >
                    <Plus size={14} /> Add Record
                </button>
            </div>

            {(isAdding || editingRecord) && (
                <div className="p-6 bg-slate-50 border border-slate-200 rounded-3xl space-y-4 animate-in fade-in slide-in-from-top-2 shadow-sm">
                    <div className="flex items-center justify-between">
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">
                            {editingRecord ? 'Edit DNS Record' : 'Add New DNS Record'}
                        </h3>
                        <button onClick={() => { setIsAdding(false); setEditingRecord(null); }} className="text-slate-400 hover:text-slate-600">
                            <X size={18} />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Type</label>
                            <select
                                value={form.type}
                                onChange={e => setForm({...form, type: e.target.value})}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white outline-none"
                            >
                                <option value="A">A (IPv4)</option>
                                <option value="AAAA">AAAA (IPv6)</option>
                                <option value="MX">MX (Mail)</option>
                                <option value="TXT">TXT (Text)</option>
                                <option value="CNAME">CNAME (Alias)</option>
                            </select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Name</label>
                            <input
                                value={form.name}
                                onChange={e => setForm({...form, name: e.target.value})}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white outline-none"
                                placeholder="@"
                            />
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Value</label>
                            <input
                                value={form.value}
                                onChange={e => setForm({...form, value: e.target.value})}
                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white outline-none"
                                placeholder="e.g. 1.2.3.4 or 2001:..."
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <button
                            onClick={() => { setIsAdding(false); setEditingRecord(null); }}
                            className="px-4 py-2 text-slate-500 text-xs font-bold uppercase tracking-widest hover:text-slate-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-6 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all"
                        >
                            Save Record
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead className="bg-slate-50 border-b border-slate-100">
                        <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <th className="px-6 py-3">Type</th>
                            <th className="px-6 py-3">Name</th>
                            <th className="px-6 py-3">Value</th>
                            <th className="px-6 py-3">TTL</th>
                            <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {records.map((rec, i) => (
                            <tr key={i} className="hover:bg-slate-50 transition-colors">
                                <td className="px-6 py-4">
                                    <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-black rounded-md uppercase">{rec.type}</span>
                                </td>
                                <td className="px-6 py-4 text-sm font-bold text-slate-700">{rec.name}</td>
                                <td className="px-6 py-4 text-sm font-mono text-slate-500 truncate max-w-xs">{rec.value}</td>
                                <td className="px-6 py-4 text-xs text-slate-400 font-medium">{rec.ttl}</td>
                                <td className="px-6 py-4 text-right">
                                    <button onClick={() => startEdit(rec)} className="p-2 text-slate-300 hover:text-blue-600 transition-colors">
                                        <RefreshCw size={14} />
                                    </button>
                                    <button onClick={() => setRecords(records.filter((_, idx) => idx !== i))} className="p-2 text-slate-300 hover:text-rose-500 transition-colors ml-2">
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const LifecycleView = ({ onExecutionStarted }: { onExecutionStarted: (id: string) => void }) => {
    const [privacyEnabled, setPrivacyEnabled] = useState(true);
    const [lockEnabled, setLockEnabled] = useState(false);
    const [autoRenewEnabled, setAutoRenewEnabled] = useState(true);

    const handleTogglePrivacy = async (enabled: boolean) => {
        try {
            const result = await moduleService.executeModule('domain_manager', {
                action: 'togglePrivacy',
                projectId: 'current_project',
                enabled: enabled,
            });
            onExecutionStarted(result.executionId);
            setPrivacyEnabled(enabled);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const handleToggleLock = async (enabled: boolean) => {
        try {
            const result = await moduleService.executeModule('domain_manager', {
                action: 'toggleLock',
                projectId: 'current_project',
                enabled: enabled,
            });
            onExecutionStarted(result.executionId);
            setLockEnabled(enabled);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const handleToggleAutoRenew = async (enabled: boolean) => {
        try {
            const result = await moduleService.executeModule('domain_manager', {
                action: 'setAutoRenew',
                projectId: 'current_project',
                enabled: enabled,
            });
            onExecutionStarted(result.executionId);
            setAutoRenewEnabled(enabled);
        } catch (err: any) {
            alert(err.message);
        }
    };

    const handleEPP = async () => {
        try {
            const result = await moduleService.executeModule('domain_manager', {
                action: 'getEPPCode',
                projectId: 'current_project',
            });
            onExecutionStarted(result.executionId);
            alert(`EPP Code retrieved: ${result.result.eppCode}`);
        } catch (err: any) {
            alert(err.message);
        }
    };

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-50 text-blue-600 rounded-xl">
                        <Lock size={20} />
                    </div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Security & Privacy</h3>
                </div>
                <p className="text-xs text-slate-400 font-medium leading-relaxed">
                    Protect your personal contact details from public WHOIS databases and prevent unauthorized transfers.
                </p>
                <div className="space-y-3 pt-4">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <span className="text-xs font-bold text-slate-700">WHOIS Privacy Protection</span>
                        <div
                            onClick={() => handleTogglePrivacy(!privacyEnabled)}
                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${privacyEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        >
                            <div className={`absolute ${privacyEnabled ? 'right-1' : 'left-1'} top-1 w-3 h-3 bg-white rounded-full transition-all`} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <span className="text-xs font-bold text-slate-700">Registry Lock</span>
                        <div
                            onClick={() => handleToggleLock(!lockEnabled)}
                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${lockEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        >
                            <div className={`absolute ${lockEnabled ? 'right-1' : 'left-1'} top-1 w-3 h-3 bg-white rounded-full transition-all`} />
                        </div>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <span className="text-xs font-bold text-slate-700">Auto-Renew Domains</span>
                        <div
                            onClick={() => handleToggleAutoRenew(!autoRenewEnabled)}
                            className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${autoRenewEnabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
                        >
                            <div className={`absolute ${autoRenewEnabled ? 'right-1' : 'left-1'} top-1 w-3 h-3 bg-white rounded-full transition-all`} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-50 text-amber-600 rounded-xl">
                        <RefreshCw size={20} />
                    </div>
                    <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight">Transfer Management</h3>
                </div>
                <p className="text-xs text-slate-400 font-medium leading-relaxed">
                    Initiate inbound transfers from other registrars or generate EPP codes for outbound migrations.
                </p>
                <div className="pt-4 space-y-3">
                    <button className="w-full p-3 bg-slate-900 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-800 transition-all">
                        Request Inbound Transfer
                    </button>
                    <button
                        onClick={handleEPP}
                        className="w-full p-3 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-slate-50 transition-all"
                    >
                        Generate EPP Code
                    </button>
                </div>
            </div>
        </div>
    );
};

export const CheckoutView = ({ domain, onExecutionStarted, onBack }: { domain: string, onExecutionStarted: (id: string) => void, onBack: () => void }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState({
        registrantName: '',
        registrantEmail: '',
        registrantAddress: '',
        registrantPhone: '',
        registrantCity: '',
        registrantState: '',
        registrantCountry: '',
        registrantZip: '',
    });

    const handleComplete = async () => {
        setLoading(true);
        try {
            const result = await moduleService.executeModule('domain_manager', {
                action: 'registerDomain',
                projectId: 'current_project',
                domain: domain,
                details: details,
            });
            onExecutionStarted(result.executionId);
            alert(`Registration for ${domain} triggered successfully!`);
            onBack();
        } catch (err: any) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const steps = [
        { id: 1, label: 'Review Domain' },
        { id: 2, label: 'Registrant Information' },
        { id: 3, label: 'Confirm & Purchase' },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                        <ShieldCheck size={20} />
                    </div>
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Secure Checkout</h2>
                </div>
                <button onClick={onBack} className="p-2 text-slate-400 hover:text-slate-800 transition-colors">
                    <ExternalLink size={20} className="rotate-180" />
                </button>
            </div>

            <div className="flex items-center justify-center gap-2">
                {steps.map((s, i) => (
                    <React.Fragment key={s.id}>
                        <div className={`flex items-center gap-2 ${step === s.id ? 'text-indigo-600' : step > s.id ? 'text-emerald-600' : 'text-slate-300'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-colors ${step === s.id ? 'bg-indigo-100 text-indigo-600' : step > s.id ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-300'}`}>
                                {s.id}
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">{s.label}</span>
                        </div>
                        {i < steps.length - 1 && (
                            <div className={`h-px w-8 sm:w-16 transition-colors ${step > s.id ? 'bg-emerald-300' : 'bg-slate-200'}`} />
                        )}
                    </React.Fragment>
                ))}
            </div>

            <div className="p-6 bg-white border border-slate-100 rounded-3xl shadow-sm space-y-6">
                {step === 1 && (
                    <>
                        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <div className="flex items-center gap-3">
                                <Globe size={20} className="text-slate-400" />
                                <span className="text-sm font-bold text-slate-700">{domain}</span>
                            </div>
                            <span className="text-xs font-black text-indigo-600 uppercase tracking-widest">Ready to Secure</span>
                        </div>

                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Price</p>
                            <p className="text-lg font-black text-slate-800 mt-1">$12.99 <span className="text-xs text-slate-400 font-medium">/year</span></p>
                        </div>

                        <div className="flex items-center justify-between gap-4 pt-4">
                            <button onClick={onBack} className="px-6 py-2 text-slate-500 text-xs font-bold uppercase tracking-widest hover:text-slate-700">
                                Cancel
                            </button>
                            <button onClick={() => setStep(2)} className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all">
                                Continue
                            </button>
                        </div>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantName}
                                    onChange={e => setDetails({...details, registrantName: e.target.value})}
                                    placeholder="John Doe"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantEmail}
                                    onChange={e => setDetails({...details, registrantEmail: e.target.value})}
                                    placeholder="john@example.com"
                                />
                            </div>
                            <div className="sm:col-span-2 space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Street Address</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantAddress}
                                    onChange={e => setDetails({...details, registrantAddress: e.target.value})}
                                    placeholder="123 Business Ave"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">City</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantCity}
                                    onChange={e => setDetails({...details, registrantCity: e.target.value})}
                                    placeholder="New York"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">State/Province</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantState}
                                    onChange={e => setDetails({...details, registrantState: e.target.value})}
                                    placeholder="NY"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Country</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantCountry}
                                    onChange={e => setDetails({...details, registrantCountry: e.target.value})}
                                    placeholder="USA"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Postal Code</label>
                                <input
                                    className="w-full px-4 py-2 rounded-xl border border-slate-200 text-sm focus:ring-2 ring-indigo-500 outline-none transition-all"
                                    value={details.registrantZip}
                                    onChange={e => setDetails({...details, registrantZip: e.target.value})}
                                    placeholder="10001"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-4 pt-4">
                            <button onClick={() => setStep(1)} className="px-6 py-2 text-slate-500 text-xs font-bold uppercase tracking-widest hover:text-slate-700">
                                Back
                            </button>
                            <button onClick={() => setStep(3)} className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all">
                                Continue
                            </button>
                        </div>
                    </>
                )}

                {step === 3 && (
                    <>
                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Domain</span>
                                <span className="text-sm font-bold text-slate-700">{domain}</span>
                            </div>
                            <div className="border-t border-slate-200" />
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Registrant</span>
                                <span className="text-xs font-medium text-slate-600">{details.registrantName || '—'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Email</span>
                                <span className="text-xs font-medium text-slate-600">{details.registrantEmail || '—'}</span>
                            </div>
                            <div className="border-t border-slate-200" />
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Total</span>
                                <span className="text-lg font-black text-slate-800">$12.99 <span className="text-xs text-slate-400 font-medium">/year</span></span>
                            </div>
                        </div>

                        <div className="flex items-center justify-between gap-4 pt-4">
                            <button onClick={() => setStep(2)} className="px-6 py-2 text-slate-500 text-xs font-bold uppercase tracking-widest hover:text-slate-700">
                                Back
                            </button>
                            <button
                                onClick={handleComplete}
                                disabled={loading}
                                className="px-8 py-3 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest hover:bg-indigo-700 transition-all disabled:opacity-50 flex items-center gap-2"
                            >
                                {loading ? <RefreshCw size={18} className="animate-spin" /> : 'Confirm & Pay'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

